import { useMemo, useState } from "react";
import { useAuth } from '../contexts/AuthContext';
import { contactService } from '../services/contactService';

// Status can be: "idle" | "sending" | "sent" | "error"

const OCCUPATION_SUGGESTIONS = [
  "Software Engineer","Registered Nurse","Hairdresser/Stylist","Electrician","Plumber",
  "Project Manager","Data Scientist","Graphic Designer","Truck Driver","Teacher",
  "Pilot","Chef","Police Officer","Carpenter","Physician/Doctor"
];

const SUPPORT_TYPES = [
  "career_guidance",
  "resume_help", 
  "salary_insights",
  "technical_issue",
  "billing_account",
  "other",
];

const SUPPORT_TYPE_LABELS = {
  career_guidance: "Career guidance",
  resume_help: "Resume / cover letter help",
  salary_insights: "Salary insights by ZIP",
  technical_issue: "Technical issue",
  billing_account: "Billing / account",
  other: "Other"
};

const EXPERIENCE_LEVELS = ["entry", "junior", "mid", "senior", "executive", "freelance"];
const ACCOUNT_TIERS = ["free", "premium"];
const DEVICE_OPTIONS = [
  "Chrome (Desktop)",
  "Safari (Mac/iOS)",
  "Edge (Windows)", 
  "Firefox",
  "Android (Chrome)",
  "Other"
];

export default function Contact() {
  const { user, userProfile } = useAuth();
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  function validate(form) {
    const data = new FormData(form);
    const e = {};
    const email = String(data?.get("email") || "");
    const name = String(data?.get("name") || "");
    const subject = String(data?.get("subject") || "");
    const message = String(data?.get("message") || "");

    if (!name?.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) e.email = "Enter a valid email.";
    if (!subject?.trim()) e.subject = "Please add a short subject.";
    if (!message?.trim()) e.message = "Tell us a bit more so we can help.";

    setErrors(e);
    return Object.keys(e)?.length === 0;
  }

  async function onSubmit(ev) {
    ev?.preventDefault();
    const form = ev?.currentTarget;
    if (!validate(form)) return;

    setStatus("sending");

    const data = new FormData(form);
    const formData = Object.fromEntries(data?.entries());

    // Honeypot early bail
    if (formData?.["company"]) {
      setStatus("sent");
      form?.reset();
      return;
    }

    try {
      // Prepare contact request data
      const requestData = {
        user_id: user?.id || null,
        name: String(formData?.name || ''),
        email: String(formData?.email || ''),
        subject: String(formData?.subject || ''),
        message: String(formData?.message || ''),
        supportType: String(formData?.supportType || 'other'),
        occupation: String(formData?.occupation || ''),
        experienceLevel: String(formData?.experienceLevel || ''),
        currentZip: String(formData?.currentZip || ''),
        targetZip: String(formData?.targetZip || ''),
        accountTier: String(formData?.accountTier || 'free'),
        topSkills: String(formData?.topSkills || ''),
        targetRole: String(formData?.targetRole || ''),
        device: String(formData?.device || ''),
        steps: String(formData?.steps || ''),
        consent: formData?.consent === 'yes'
      };

      const result = await contactService?.submitContactRequest(requestData);
      
      if (result?.error) {
        setStatus("error");
        return;
      }

      setStatus("sent");
      form?.reset();
    } catch (error) {
      setStatus("error");
    }
  }

  const year = useMemo(() => new Date()?.getFullYear(), []);

  // Pre-fill form with user profile data if available
  const defaultValues = useMemo(() => ({
    name: userProfile?.full_name || '',
    email: userProfile?.email || user?.email || '',
    occupation: userProfile?.occupation_id ? '' : '', // Will need occupation name lookup
    currentZip: userProfile?.current_zip || '',
    targetZip: userProfile?.target_zip || '',
    experienceLevel: userProfile?.seniority_level || 'mid',
    accountTier: userProfile?.role === 'premium' ? 'premium' : 'free'
  }), [user, userProfile]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-12">
      <div className="mb-2">
        <h1 className="text-3xl font-semibold tracking-tight">Contact Us</h1>
        <p className="text-gray-600 mt-2">
          Tell us a little about your situation and we will point you to the best help.
        </p>
        {user && (
          <p className="text-sm text-green-600 mt-2">
            ✓ Signed in as {userProfile?.full_name || user?.email}
          </p>
        )}
      </div>
      <form onSubmit={onSubmit} className="space-y-8">
        {/* Honeypot */}
        <input name="company" className="hidden" tabIndex={-1} autoComplete="off" />

        {/* — General info — */}
        <fieldset className="border border-gray-200 rounded-lg p-5">
          <legend className="px-2 text-sm font-semibold text-gray-700">General Info</legend>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1 text-gray-700">Full Name</label>
              <input 
                name="name" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                defaultValue={defaultValues?.name}
                required 
              />
              {errors?.name && <p className="text-red-600 text-sm mt-1">{errors?.name}</p>}
            </div>
            <div>
              <label className="block text-sm mb-1 text-gray-700">Email</label>
              <input 
                name="email" 
                type="email" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                defaultValue={defaultValues?.email}
                required 
              />
              {errors?.email && <p className="text-red-600 text-sm mt-1">{errors?.email}</p>}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm mb-1 text-gray-700">Subject</label>
            <input 
              name="subject" 
              className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
              placeholder="Short title of your request" 
              required 
            />
            {errors?.subject && <p className="text-red-600 text-sm mt-1">{errors?.subject}</p>}
          </div>

          <div className="mt-4">
            <label className="block text-sm mb-1 text-gray-700">Support Type</label>
            <select name="supportType" className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              {SUPPORT_TYPES?.map((t) => (
                <option key={t} value={t} className="bg-white text-gray-900">
                  {SUPPORT_TYPE_LABELS?.[t]}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        {/* — Career context — */}
        <fieldset className="border border-gray-200 rounded-lg p-5">
          <legend className="px-2 text-sm font-semibold text-gray-700">Career Context</legend>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1 text-gray-700">Current Occupation / Role</label>
              <input 
                name="occupation" 
                list="occupations" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="e.g., Registered Nurse"
                defaultValue={defaultValues?.occupation}
              />
              <datalist id="occupations">
                {OCCUPATION_SUGGESTIONS?.map(o => <option key={o} value={o} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-sm mb-1 text-gray-700">Experience Level</label>
              <select 
                name="experienceLevel" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                defaultValue={defaultValues?.experienceLevel}
              >
                {EXPERIENCE_LEVELS?.map(level => (
                  <option key={level} value={level} className="bg-white text-gray-900">
                    {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm mb-1 text-gray-700">Current ZIP</label>
              <input 
                name="currentZip" 
                inputMode="numeric" 
                pattern="\d{5}" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="e.g., 15206"
                defaultValue={defaultValues?.currentZip}
              />
            </div>
            <div>
              <label className="block text-sm mb-1 text-gray-700">Target ZIP (moving?)</label>
              <input 
                name="targetZip" 
                inputMode="numeric" 
                pattern="\d{5}" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Optional"
                defaultValue={defaultValues?.targetZip}
              />
            </div>
            <div>
              <label className="block text-sm mb-1 text-gray-700">Account Type</label>
              <select 
                name="accountTier" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                defaultValue={defaultValues?.accountTier}
              >
                {ACCOUNT_TIERS?.map(tier => (
                  <option key={tier} value={tier} className="bg-white text-gray-900">
                    {tier?.charAt(0)?.toUpperCase() + tier?.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm mb-1 text-gray-700">Top 3 skills to highlight</label>
              <input 
                name="topSkills" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="e.g., Leadership, Color-correction, Sales" 
              />
            </div>
            <div>
              <label className="block text-sm mb-1 text-gray-700">Target role you are aiming for</label>
              <input 
                name="targetRole" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="e.g., Senior Data Scientist" 
              />
            </div>
          </div>
        </fieldset>

        {/* — Technical context (only if they have an issue) — */}
        <fieldset className="border border-gray-200 rounded-lg p-5">
          <legend className="px-2 text-sm font-semibold text-gray-700">If this is a technical issue</legend>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1 text-gray-700">Browser / Device</label>
              <select name="device" className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                {DEVICE_OPTIONS?.map(device => (
                  <option key={device} value={device} className="bg-white text-gray-900">{device}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1 text-gray-700">Steps to reproduce (optional)</label>
              <input 
                name="steps" 
                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                placeholder="What you clicked / expected / saw" 
              />
            </div>
          </div>
        </fieldset>

        {/* — Message — */}
        <div>
          <label className="block text-sm mb-1 text-gray-700">Message</label>
          <textarea 
            name="message" 
            rows={6} 
            className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical" 
            placeholder="Give us context so we can jump straight to helping you." 
            required 
          />
          {errors?.message && <p className="text-red-600 text-sm mt-1">{errors?.message}</p>}
        </div>

        {/* Consent */}
        <label className="flex items-start gap-2 text-sm text-gray-600">
          <input 
            type="checkbox" 
            name="consent" 
            value="yes" 
            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
            defaultChecked 
          />
          I agree that my information may be used to respond to my request in accordance with the Privacy Policy.
        </label>

        <div className="flex items-center gap-4">
          <button
            disabled={status === "sending"}
            className="rounded px-5 py-2.5 text-white hover:opacity-90 disabled:opacity-60 transition-opacity"
            style={{
              backgroundColor: '#2285C8',
            }}
          >
            {status === "sending" ? "Sending…" : "Send Message"}
          </button>
          {status === "sent" && <span className="text-green-600">Thanks! We will get back to you shortly.</span>}
          {status === "error" && <span className="text-red-600">Could not send. Please try again.</span>}
        </div>

        <p className="text-xs text-gray-400 mt-6">© {year} Epoxy Dogs LLC. SkillBinder™</p>
      </form>
    </main>
  );
}